/**
 * Economics API Service
 * Frontend client for the Economics CBA & Investment module.
 */
import axios from 'axios';

// Economics routes live at /api/... (not /api/v1/...), so strip the v1 suffix
const _ECON_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1')
  .replace(/\/api\/v1\/?$/, '');

const API = axios.create({ baseURL: _ECON_BASE });

API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Types ──

export interface CapexItem {
  id: string;
  category: string;
  description: string;
  unit_cost: number;
  currency: string;
  quantity: number;
  total: number;
}

export interface OpexItem {
  id: string;
  category: string;
  description: string;
  unit_cost: number;
  currency: string;
  quantity: number;
  labor_cost_pct: number;
  total: number;
}

export interface InvestmentData {
  capex_items: CapexItem[];
  opex_items: OpexItem[];
  project_currency: string;
  exchange_rate: number;
  total_capex?: number;
  total_opex_annual?: number;
  personnel_subtotal?: number;
  non_personnel_subtotal?: number;
}

export interface CashFlowYear {
  year: number;
  capex: number;
  opex: number;
  benefits: number;
  net: number;
  cumulative: number;
  discounted_net: number;
  discounted_cumulative: number;
  maturity_factor: number;
}

export interface CBAResult {
  horizon_years: number;
  discount_rate: number;
  npv: number;
  irr: number | null;
  payback_simple: number | null;
  payback_discounted: number | null;
  bcr: number;
  total_capex: number;
  total_opex: number;
  total_benefits: number;
  total_net: number;
  cash_flows: CashFlowYear[];
  currency: string;
}

export interface CategoryDef {
  label_es: string;
  label_en: string;
  icon: string;
  color: string;
}

export interface ScenarioKPIs {
  scenario_id: string;        // 'as_is' | 'ams' | 'fastgate' | 'infrastructure'
  label_es: string;
  label_en: string;
  delta_taxi_out_min: number;
  delta_taxi_in_min: number;
  delta_capacity_pct: number;
  annual_fuel_savings: number;
  annual_delay_savings: number;
  annual_total_savings: number;
  capex_assumption: number;
  npv_10y: number;
  irr: number | null;
  payback_years: number | null;
  bcr: number;
  currency: string;
}

export interface ScenarioComparisonResult {
  project_id: string;
  daily_flights: number;
  annual_flights: number;
  scenarios: ScenarioKPIs[];
  generated_at: string;
}

// ── API Calls ──

export const economicsAPI = {
  // Project-scoped
  getEconomics: (projectId: string) =>
    API.get(`/api/projects/${projectId}/economics`).then(r => r.data),

  saveInvestment: (projectId: string, data: InvestmentData) =>
    API.post(`/api/projects/${projectId}/economics/investment`, data).then(r => r.data),

  calculateCBA: (projectId: string, params?: Record<string, any>) =>
    API.post(`/api/projects/${projectId}/economics/calculate`, params || {}).then(r => r.data),

  getSensitivity: (projectId: string) =>
    API.get(`/api/projects/${projectId}/economics/sensitivity`).then(r => r.data),

  exportBusinessCase: async (projectId: string, language = 'es') => {
    const res = await API.post(
      `/api/projects/${projectId}/economics/export`,
      { language },
      { responseType: 'blob' }
    );
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Business_Case_${projectId.slice(0, 8)}.docx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  getScenarioComparison: (projectId: string, params?: Record<string, any>): Promise<ScenarioComparisonResult> =>
    API.post(`/api/projects/${projectId}/economics/scenario-comparison`, params || {}).then(r => r.data.data),

  // Liberated-capacity revenue channel (measured taxi ceiling → freed movements → revenue).
  getCapacityValue: (projectId: string, params?: Record<string, any>) =>
    API.post(`/api/projects/${projectId}/economics/capacity-value`, params || {}).then(r => r.data.data),

  // Global
  getReferenceValues: () =>
    API.get('/api/economics/reference-values').then(r => r.data),

  getLaborCosts: () =>
    API.get('/api/economics/labor-costs').then(r => r.data),

  getCategories: () =>
    API.get('/api/economics/categories').then(r => r.data),
};

export default economicsAPI;
